import React, { useCallback, useEffect, useState, useContext } from "react";
import { Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import BackLinkBase from "@/components/BackLink";
import ExtendSettingBase from "./ExtendSetting";
import ExtendConfirmationBase from "./ExtendConfirmation";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { HTTP_STATUS } from "@/config/constants";
import { logger } from "@/helpers/loggerHelper";
import NoDataFoundBase from "@/components/NoDataFound";
import RpSvgIconBase from "@/components/icons/RpSvgIcon";
import sortBy from "lodash/sortBy";
import RpAlertBase from "@/components/alerts/RpAlert";

type Id = number | string;

type ExtendCampaignProps = {
  campaignId?: Id;
};

type LoggedInUser = {
  participantData?: {
    id?: Id;
  };
};

type ExtendLocation = {
  location_id: Id;
  location_name?: string;
  extend_start_date?: string;
  extend_end_date?: string;
  extend_budget?: number | string;
  [key: string]: unknown;
};

type ExtendCampaignData = {
  campaign_name?: string;
  location_data?: ExtendLocation[];
  [key: string]: unknown;
};

type ContinueFormData = {
  selectedLoc: Record<string, ExtendLocation>;
};

type FetchError = {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
};

const BackLink = BackLinkBase as React.ComponentType<Record<string, unknown>>;

const ExtendSetting = ExtendSettingBase as unknown as React.ComponentType<
  Record<string, unknown>
>;

const ExtendConfirmation =
  ExtendConfirmationBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

const NoDataFound = NoDataFoundBase as React.ComponentType<
  Record<string, unknown>
>;

const RpSvgIcon = RpSvgIconBase as React.ComponentType<Record<string, unknown>>;

const RpAlert = RpAlertBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

function ExtendCampaign({ campaignId }: ExtendCampaignProps) {
  const { loggedInUser } = useContext(GlobalContext) as {
    loggedInUser?: LoggedInUser;
  };
  const loader = useContext(LoaderContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [campaignData, setCampaignData] = useState<ExtendCampaignData | null>(
    null
  );
  const [selections, setSelections] = useState<ExtendLocation[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaignDetails = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const reqData = {
        campaign_id: campaignId,
        participant_id: loggedInUser?.participantData?.id || "",
      };
      const res = await campaignService.getCampaignExtensionLocations(reqData);
      if (res.status === HTTP_STATUS.OK) {
        const data = res.data as ExtendCampaignData;
        if (data && data?.location_data?.length) {
          data.location_data = sortBy(data?.location_data, "location_id");
          setCampaignData(data);
        }
      }
    } catch (err) {
      logger.error(err);
      const error = err as FetchError;
      setError(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong"
      );
      setCampaignData(null);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId, loggedInUser]);

  useEffect(() => {
    fetchCampaignDetails();
  }, [fetchCampaignDetails]);

  useEffect(() => {
    loader(isLoading);
  }, [isLoading, loader]);

  const handleOnContinueToConfirm = useCallback((data: ContinueFormData) => {
    const filledLocData = Object.values(data.selectedLoc).filter((value) => {
      return (
        value.extend_start_date && value.extend_end_date && value.extend_budget
      );
    });

    setSelections(filledLocData);
    if (filledLocData?.length) setStep(3);
  }, []);

  const handleOnMakeChanges = useCallback(() => {
    setStep(1);
  }, []);

  const handleOnBack = useCallback(() => {
    if (step !== 1) {
      setStep(step - 1);
    } else {
      navigate(-1);
    }
  }, [step, navigate]);

  const renderDatesBudgetsScreen = () => {
    return (
      <div className={`${step === 1 || step === 2 ? "d-block" : "d-none"}`}>
        <Typography
          variant="h6"
          component="h1"
          className="widget-title mb-5 mb-md-auto"
          gutterBottom
        >
          <RpSvgIcon name="time_plus" />{" "}
          {`Extend ${campaignData?.campaign_name ? campaignData?.campaign_name : "Campaign"} Campaign`}
        </Typography>
        <Typography variant="caption" gutterBottom>
          <span className="fw-sb">This is an active campaign.</span> You can
          extend it to keep your ads running or schedule new campaign dates.
          Choose your new start and end dates below. Dates may be marked
          eligible or ineligible for creative updates based on the extension
          length. Tooltips and labels will guide you.
        </Typography>

        <ExtendSetting
          campaignData={campaignData}
          onContinueToConfirm={handleOnContinueToConfirm}
          step={step}
          setStep={setStep}
        />
      </div>
    );
  };

  const renderConfirmationScreen = () => {
    return (
      <div className={`${step === 3 ? "d-block" : "d-none"}`}>
        <Typography
          variant="h5"
          color="text.primary"
          className="widget-title mb-4 mb-md-auto"
          gutterBottom
        >
          Confirmation
        </Typography>
        <Typography variant="caption" gutterBottom>
          Review your selections and press confirm if it looks good
        </Typography>
        <ExtendConfirmation
          selections={selections}
          campaignData={campaignData}
          onMakeChanges={handleOnMakeChanges}
        />
      </div>
    );
  };

  if (isLoading) {
    return <NoDataFound message="Loading..." />;
  }

  if (error) {
    return (
      <div className="d-flex-center w-100 py-5">
        <RpAlert severity="error">{error}</RpAlert>
      </div>
    );
  }

  return (
    <div className="dashboard participant-dashboard extend-campaign pb-5">
      {step !== 3 && <BackLink onClick={handleOnBack} />}
      {renderDatesBudgetsScreen()}

      {renderConfirmationScreen()}
    </div>
  );
}

export default ExtendCampaign;
